import React, { useEffect, useState, useCallback } from "react";
import { ManagerHeader } from "../components/ManagerHeader";
import { api, fmtNGN } from "../lib/api";
import { CardSkeleton, EmptyState, ErrorState } from "../components/LoadState";
import { ClipboardList, Package, Coins, Plus, Calendar, Truck as TruckIcon, Users } from "lucide-react";
import { NewEntryModal } from "./NewEntryModal";
import { toast } from "sonner";

export default function ManagerDashboard() {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/dashboard/manager");
      setData(data);
    } catch (e) {
      const msg = e.userMessage || "Failed to load dashboard.";
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const today = new Date().toLocaleDateString("en-US", { weekday: "long", year: "numeric", month: "long", day: "numeric" });

  return (
    <div className="min-h-screen bg-slate-50 pb-24" data-testid="manager-dashboard">
      <ManagerHeader />
      <div className="max-w-2xl mx-auto px-4 -mt-2">
        <div className="bg-white rounded-xl border border-border shadow-sm px-4 py-3 flex items-center gap-2 text-sm text-muted-foreground mb-4">
          <Calendar size={16} /> {today}
        </div>

        {error ? <div className="mb-4"><ErrorState message={error} onRetry={load} /></div> : null}

        {/* Stats */}
        <div className="grid grid-cols-3 gap-3 mb-5">
          <StatBox
            label="Entries Today"
            value={data?.entries_today ?? "—"}
            icon={<ClipboardList size={18} />}
            testId="stat-entries"
          />
          <StatBox
            label="Crates"
            value={data?.crates_today ?? "—"}
            icon={<Package size={18} />}
            testId="stat-crates"
          />
          <StatBox
            label="Earnings"
            value={data ? fmtNGN(data.earnings_today) : "—"}
            icon={<Coins size={18} />}
            accent
            testId="stat-earnings"
          />
        </div>

        {/* Activity */}
        <div className="mb-3 flex items-center gap-2 text-sm text-muted-foreground">
          <span className="w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center">
            <TruckIcon size={12} />
          </span>
          Today&apos;s Activity
        </div>

        <div className="space-y-3" data-testid="today-activity-list">
          {loading ? <CardSkeleton rows={3} /> : null}
          {!loading && (data?.today_entries || []).length === 0 ? (
            <EmptyState title="No entries yet today" message="Tap + to add your first." />
          ) : null}
          {!loading && (data?.today_entries || []).map((e) => <EntryCard key={e.id} entry={e} />)}
        </div>
      </div>

      {/* FAB */}
      <button
        data-testid="new-entry-fab"
        onClick={() => setOpen(true)}
        className="fixed bottom-6 right-6 w-14 h-14 rounded-full bg-primary text-primary-foreground shadow-xl flex items-center justify-center hover:brightness-110 active:scale-95 transition"
      >
        <Plus size={24} />
      </button>

      <NewEntryModal
        open={open}
        onClose={() => setOpen(false)}
        onCreated={() => { setOpen(false); load(); toast.success("Entry saved"); }}
      />
    </div>
  );
}

function StatBox({ label, value, icon, accent, testId }) {
  return (
    <div
      data-testid={testId}
      className={`rounded-xl border p-3 ${accent ? "bg-emerald-50 border-emerald-100" : "bg-white border-border"}`}
    >
      <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${accent ? "bg-emerald-100 text-emerald-600" : "bg-primary/10 text-primary"}`}>
        {icon}
      </div>
      <div className={`mt-2 text-xl font-display font-bold ${accent ? "text-emerald-600" : "text-foreground"}`}>
        {value}
      </div>
      <div className="text-[11px] text-muted-foreground">{label}</div>
    </div>
  );
}

function EntryCard({ entry }) {
  const time = new Date(entry.created_at).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" });
  const load = entry.loads[0];
  const staffNames = load ? load.staff.map((s) => s.staff_name.split(" ")[0]).join(" & ") : "";
  return (
    <div className="bg-white rounded-xl border border-border shadow-sm flex gap-3 p-3 animate-fade-in" data-testid={`entry-${entry.id}`}>
      <div className="w-16 shrink-0 text-center">
        <div className="text-[11px] font-semibold tracking-wider text-primary">{time}</div>
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="font-semibold text-sm flex items-center gap-1.5 truncate">
              <Users size={13} className="text-muted-foreground shrink-0" /> {staffNames}
            </div>
            <div className="text-xs text-muted-foreground mt-0.5 flex items-center flex-wrap gap-x-3 gap-y-0.5">
              <span className="inline-flex items-center gap-1"><TruckIcon size={11} /> {entry.plate_number}</span>
              <span>{load?.product_name}</span>
            </div>
            <div className="text-xs text-muted-foreground mt-0.5">
              {fmtNGN(load?.rate_per_crate)} × {load?.total_crates} crates
            </div>
          </div>
          <div className="text-right shrink-0">
            <div className="text-money text-base">{fmtNGN(entry.total_amount)}</div>
            {load?.staff?.length ? (
              <div className="text-[11px] text-muted-foreground">{fmtNGN(load.staff[0].earning_share)} each</div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
